/**
 * The state of a pet at a given time, computed from its log (docs/SPEC.md section 3). Pure: the same log and time
 * always give the same state.
 *
 * Each meter is a list of segments, one per care action (plus the hatch). A segment starts at the care time with the
 * level the meter had at that moment, climbs in a straight line to 100% over the care action's `fill` (instantly
 * when there is none) and then decays along the meter's curve, with the decay clock starting when the climb ends.
 */
import { cleanFill } from "./log";
import { deathHours, hoursAtValue, makeCurve, meterValue, type Curve } from "./curve";
import { makeEffectiveClock, type EffectiveClock } from "./effective-time";
import { METER_IDS, type MeterId, type PetLog } from "./types";

export interface MeterState {
  /** 0 to 100. Every meter is 0 once the pet has died. */
  value: number;
  /** Effective hours of decay: since this meter's last refill finished (0 while it is still refilling). */
  effectiveHours: number;
  lastCareAt: number;
  /** True while a gradual refill is climbing (the pet is "sleeping" for Rest). */
  refilling: boolean;
}

export interface PetState {
  alive: boolean;
  /** When the pet died (milliseconds), or null while it is alive. */
  diedAt: number | null;
  /** The time the state describes: `now`, or the time of death if the pet died before `now`. */
  evaluatedAt: number;
  /** True while any meter is in the last chance (at 1%, before the pet dies). */
  lastChance: boolean;
  meters: Record<MeterId, MeterState>;
}

/** One care action (or the hatch) of one meter. */
interface Segment {
  /** When the care happened. */
  at: number;
  /** The meter level at that moment (100 at the hatch). */
  base: number;
  /** When the climb ends and the decay clock starts (equals `at` for an instant refill). */
  end: number;
  /** Effective hours the decay clock already stands at when the segment ends: 0, or the place on the curve a woken-up meter holds. */
  offset: number;
}

/** A log with everything that does not depend on the evaluation time worked out once. */
export interface PreparedPet {
  log: PetLog;
  clock: EffectiveClock;
  curves: Record<MeterId, Curve>;
  /** Segments per meter, ascending by `at`, starting with the hatch. */
  segments: Record<MeterId, Segment[]>;
  /** When the pet dies given all the care in the log, or null if it never does. */
  death: number | null;
}

/** The level while a segment is still climbing (`t` is before `seg.end`). */
function climbing(seg: Segment, t: number): number {
  return seg.base + ((100 - seg.base) * (t - seg.at)) / (seg.end - seg.at);
}

export function preparePet(log: PetLog): PreparedPet {
  const clock = makeEffectiveClock(log.sessions);
  const curves = {} as Record<MeterId, Curve>;
  const segments = {} as Record<MeterId, Segment[]>;
  let death: number | null = null;
  for (const meter of METER_IDS) {
    const curve = makeCurve(log.curves[meter] ?? {});
    curves[meter] = curve;
    const events = log.care
      .filter((c) => c.meter === meter && c.at >= log.hatchedAt)
      .sort((a, b) => a.at - b.at);
    const list: Segment[] = [{ at: log.hatchedAt, base: 100, end: log.hatchedAt, offset: 0 }];
    segments[meter] = list;

    // One pass: build the segments and find the death clock for this meter, which is the first interval between
    // care actions that lasts long enough, counted from the end of the refill that started it. (`between` is linear
    // in the number of sessions, so it is asked once per care action.) A wake-up (`stop`) ends a sleeping refill where it
    // is; the meter then decays from that level, as if it had already been falling for `offset` hours.
    let segment = list[0] as Segment;
    let died = false;
    for (let i = 0; i <= events.length && !died; i++) {
      const next = events[i];
      const nextCare = next ? next.at : Infinity;
      const limit = deathHours(curve) - segment.offset;
      // Decay hours between the end of this refill and the next care (0 if the next care came during the refill).
      const hours = next && nextCare > segment.end ? clock.between(segment.end, nextCare) : 0;
      if (next && hours < limit) {
        if (next.stop === true) {
          // Only a refill in progress can be stopped; otherwise the wake-up changes nothing.
          if (nextCare < segment.end) {
            const level = Math.max(0, climbing(segment, nextCare));
            segment = { at: next.at, base: level, end: next.at, offset: hoursAtValue(level, curve) };
            list.push(segment);
          }
          continue;
        }
        // Cared for in time: the next segment starts from the level this one had at that moment.
        const level = nextCare < segment.end ? climbing(segment, nextCare) : meterValue(segment.offset + hours, curve);
        segment = { at: next.at, base: Math.max(0, level), end: next.at + (cleanFill(next.fill) ?? 0), offset: 0 };
        list.push(segment);
        continue;
      }
      const diesAt = clock.timeWhen(segment.end, limit);
      if (diesAt < nextCare) {
        if (death === null || diesAt < death) death = diesAt;
        died = true; // care after the death time does not count
      } else if (next && next.stop !== true) {
        segment = { at: next.at, base: 0, end: next.at + (cleanFill(next.fill) ?? 0), offset: 0 };
        list.push(segment);
      }
    }
  }
  return { log, clock, curves, segments, death };
}

export function stateAt(prepared: PreparedPet, now: number): PetState {
  const { log, clock, curves, segments, death } = prepared;
  const dead = death !== null && now >= death;
  const evaluatedAt = Math.max(log.hatchedAt, dead ? (death as number) : now);

  let lastChance = false;
  const meters = {} as Record<MeterId, MeterState>;
  for (const meter of METER_IDS) {
    const list = segments[meter];
    // The latest care that is not after the evaluation time (binary search; the list is ascending).
    let lo = 0;
    let hi = list.length - 1;
    while (lo < hi) {
      const mid = (lo + hi + 1) >> 1;
      if ((list[mid] as Segment).at <= evaluatedAt) lo = mid;
      else hi = mid - 1;
    }
    const seg = list[lo] as Segment;
    const refilling = evaluatedAt < seg.end;
    const effectiveHours = refilling ? 0 : seg.offset + clock.between(seg.end, evaluatedAt);
    const curve = curves[meter];
    meters[meter] = {
      // The hours are worked out once: the effective clock is the expensive part of the per-minute score loop.
      value: dead ? 0 : refilling ? climbing(seg, evaluatedAt) : meterValue(effectiveHours, curve),
      effectiveHours,
      lastCareAt: seg.at,
      refilling: refilling && !dead,
    };
    if (!dead && !refilling && effectiveHours >= curve.onePercentAt) lastChance = true;
  }
  return { alive: !dead, diedAt: dead ? (death as number) : null, evaluatedAt, lastChance, meters };
}

export function petStateAt(log: PetLog, now: number): PetState {
  return stateAt(preparePet(log), now);
}
